import { Component, DestroyRef, inject, input, OnChanges, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { Icon } from './icon';
import { I18nService } from './i18n.service';

export type DocumentValidationStatus = 'PENDING' | 'VALIDATED' | 'REJECTED';
export type DocumentType = 'ID_CARD' | 'PASSPORT' | 'MEDICAL_PRESCRIPTION' | 'INVOICE' | 'OTHER';

export interface RecordDocument {
  id: number;
  filename: string;
  contentType: string;
  documentType: DocumentType;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
  deletable: boolean;
  validationStatus: DocumentValidationStatus;
  version: number;
  canValidate: boolean;
}

export interface DocumentValidationEvent {
  id: number;
  documentId: number;
  filename: string;
  documentType: DocumentType;
  action: 'UPLOADED' | 'STATUS_CHANGED' | 'DELETED';
  previousStatus: DocumentValidationStatus | null;
  status: DocumentValidationStatus;
  changedAt: string;
  changedBy: string;
  changedByName?: string | null;
  reason: string | null;
}

@Component({
  selector: 'ipm-record-documents',
  imports: [Icon, TranslatePipe],
  template: `
    <section class="documents" [attr.aria-label]="'Documents' | translate">
      <div class="heading">
        <h3><button type="button" class="toggle" [attr.aria-expanded]="opened()" [disabled]="!recordId()" (click)="toggle()"><ipm-icon name="file" />{{ 'Documents' | translate }}@if (loaded()) { <span class="count">{{ documents().length }}</span> }<ipm-icon name="chevron-down" /></button></h3>
        @if (opened()) {
          <div class="actions">
            <button type="button" class="icon-button" [title]="'Add document' | translate" [attr.aria-label]="'Add document' | translate" [disabled]="busy() || !loaded() || uploadFormOpen()" (click)="openUploadForm()"><ipm-icon name="plus" /></button>
            <button type="button" class="icon-button" [title]="'Refresh documents' | translate" [attr.aria-label]="'Refresh documents' | translate" [disabled]="busy()" (click)="load()"><ipm-icon name="refresh" /></button>
            <button type="button" class="icon-button" [title]="'Document history' | translate" [attr.aria-label]="'Document history' | translate" [disabled]="busy()" (click)="showHistory()"><ipm-icon name="history" /></button>
          </div>
        }
      </div>
      @if (opened()) {
        <div [attr.aria-busy]="busy()">
          @if (error()) { <p class="alert error" role="alert">{{ error() | translate }}</p> }
          @if (notice()) { <p class="alert success" role="status">{{ notice() | translate }}</p> }
          @if (uploadFormOpen()) {
            <form class="upload-form" [attr.aria-label]="'Add document' | translate" (submit)="$event.preventDefault(); upload()">
              <h4>{{ 'Add document' | translate }}</h4>
              <label class="upload-type">{{ 'Document type' | translate }}<select required [value]="uploadType()" [disabled]="busy()" (change)="setUploadType($event)">
                <option value="" disabled>{{ 'Select document type' | translate }}</option>
                @for (option of documentTypes; track option.value) { <option [value]="option.value">{{ option.label | translate }}</option> }
              </select></label>
              <label class="upload-file">{{ 'Document file' | translate }}<input type="file" required accept="application/pdf,image/jpeg,image/png" [disabled]="busy() || !uploadType()" (change)="selectFile($event)" /></label>
              <div class="actions"><button type="button" class="secondary" [disabled]="busy()" (click)="cancelUpload()">{{ 'Cancel' | translate }}</button><button type="submit" class="primary" [disabled]="busy() || !uploadType() || !selectedFile()"><ipm-icon name="plus" />{{ (busy() ? 'Uploading document...' : 'Upload document') | translate }}</button></div>
            </form>
          }
          @if (busy()) { <p class="muted" role="status">{{ (uploadFormOpen() ? 'Uploading document...' : 'Loading documents...') | translate }}</p> }
          @if (pendingDelete(); as pending) {
            <div class="confirmation" role="alertdialog" [attr.aria-label]="'Delete document?' | translate">
              <p>{{ 'Delete document?' | translate }} <strong>{{ pending.filename }}</strong></p>
              <div class="actions"><button type="button" class="secondary" [disabled]="busy()" (click)="pendingDelete.set(null)">{{ 'Cancel' | translate }}</button><button type="button" class="primary destructive" [disabled]="busy()" (click)="remove()"><ipm-icon name="trash" />{{ 'Confirm' | translate }}</button></div>
            </div>
          }
          @if (reviewDocument(); as reviewing) {
            <section class="review" [attr.aria-label]="'Review document' | translate">
              <h4>{{ 'Review document' | translate }}: {{ reviewing.filename }}</h4>
              <label>{{ 'Validation status' | translate }}<select [value]="reviewStatus()" [disabled]="busy()" (change)="setReviewStatus($event)">
                @for (status of validationStatuses; track status) { <option [value]="status" [disabled]="status === reviewing.validationStatus">{{ statusLabel(status) | translate }}</option> }
              </select></label>
              <label>{{ 'Review reason' | translate }}<textarea rows="3" maxlength="1000" required [value]="reviewReason()" [disabled]="busy()" (input)="setReviewReason($event)"></textarea></label>
              <div class="actions"><button type="button" class="secondary" [disabled]="busy()" (click)="reviewDocument.set(null)">{{ 'Cancel' | translate }}</button><button type="button" class="primary" [disabled]="busy() || !reviewReason().trim() || reviewStatus() === reviewing.validationStatus" (click)="saveValidation()"><ipm-icon name="check" />{{ 'Save validation' | translate }}</button></div>
            </section>
          }
          <ul>
            @for (document of documents(); track document.id) {
              <li><ipm-icon name="file" /><div class="file-info"><strong>{{ document.filename }}</strong><small>{{ documentTypeLabel(document.documentType) | translate }} &middot; {{ sizeLabel(document.size) }} &middot; {{ dateLabel(document.uploadedAt) }} &middot; {{ document.uploadedBy }}</small><span class="badge" [class.pending]="document.validationStatus === 'PENDING'" [class.rejected]="document.validationStatus === 'REJECTED'">{{ statusLabel(document.validationStatus) | translate }}</span></div><div class="actions">
                @if (document.canValidate) { <button type="button" class="icon-button" [title]="'Review document' | translate" [attr.aria-label]="('Review document' | translate) + ': ' + document.filename" [disabled]="busy()" (click)="openReview(document)"><ipm-icon name="edit" /></button> }
                <button type="button" class="icon-button" [title]="'Validation history' | translate" [attr.aria-label]="('Validation history' | translate) + ': ' + document.filename" [disabled]="busy()" (click)="showHistory(document)"><ipm-icon name="history" /></button>
                <button type="button" class="icon-button" [title]="'Download document' | translate" [attr.aria-label]="('Download document' | translate) + ': ' + document.filename" [disabled]="busy()" (click)="download(document)"><ipm-icon name="download" /></button>
                @if (document.deletable) { <button type="button" class="icon-button danger" [title]="'Delete document' | translate" [attr.aria-label]="('Delete document' | translate) + ': ' + document.filename" [disabled]="busy()" (click)="pendingDelete.set(document)"><ipm-icon name="trash" /></button> }
              </div></li>
            }
          </ul>
          @if (historyOpen()) {
            <section class="history" [attr.aria-label]="'Validation history' | translate">
              <div class="heading"><h4>{{ 'Validation history' | translate }}@if (historyDocument(); as selected) { : {{ selected.filename }} }</h4><button type="button" class="icon-button" [title]="'Close history' | translate" [attr.aria-label]="'Close history' | translate" (click)="historyOpen.set(false)"><ipm-icon name="close" /></button></div>
              <ol>@for (event of historyEvents(); track event.id) {
                <li class="history-event"><div class="file-info"><strong>{{ actionLabel(event.action) | translate }}: {{ event.filename }}</strong><small>{{ documentTypeLabel(event.documentType) | translate }} &middot; {{ dateLabel(event.changedAt) }} &middot; {{ event.changedByName?.trim() || event.changedBy }}</small><p class="transition">@if (event.action === 'STATUS_CHANGED' && event.previousStatus) { {{ statusLabel(event.previousStatus) | translate }} &rarr; }{{ statusLabel(event.status) | translate }}</p>@if (event.reason) { <p class="reason">{{ event.reason }}</p> }</div></li>
              } @empty { @if (!busy()) { <li class="muted">{{ 'No validation history' | translate }}</li> } }</ol>
            </section>
          }
          @if (loaded() && !documents().length && !busy()) { <p class="muted">{{ 'No documents' | translate }}</p> }
          @if (error() && !loaded()) { <button type="button" class="secondary" [disabled]="busy()" (click)="load()"><ipm-icon name="refresh" />{{ 'Retry' | translate }}</button> }
        </div>
      }
    </section>
  `,
  styles: `
    :host { display: block; min-width: 0; }
    .documents { border-top: 1px solid var(--line); margin-top: 20px; padding-top: 10px; }
    .heading, .actions { display: flex; align-items: center; gap: 6px; }
    .heading { justify-content: space-between; gap: 12px; }
    h3 { margin: 0; min-width: 0; font-size: 14px; }
    .toggle { display: flex; align-items: center; gap: 8px; padding: 8px 0; border: 0; background: transparent; color: var(--ink); text-align: left; }
    .count { font-size: 12px; color: var(--muted); }
    ul { list-style: none; padding: 0; margin: 8px 0; }
    li { display: flex; align-items: center; gap: 10px; border-bottom: 1px solid var(--table-line); padding: 12px 0; }
    li > ipm-icon { color: var(--icon); }
    .file-info { flex: 1; min-width: 0; overflow-wrap: anywhere; }
    strong { font-size: 12px; font-weight: 600; overflow-wrap: anywhere; }
    small { display: block; margin-top: 5px; color: var(--muted); font-size: 11px; }
    .muted { color: var(--muted); font-size: 12px; }
    .confirmation { border-left: 3px solid var(--danger); padding: 8px 12px; background: var(--surface); }
    .confirmation p { margin: 0 0 10px; font-size: 12px; overflow-wrap: anywhere; }
    .badge { margin-top: 7px; }
    .badge.rejected { background: var(--error-bg); color: var(--error-ink); }
    .review, .history, .upload-form { border-top: 1px solid var(--line); margin-top: 12px; padding: 12px 0; }
    h4 { margin: 0 0 12px; font-size: 13px; font-weight: 600; overflow-wrap: anywhere; }
    .review label { display: grid; gap: 6px; margin-bottom: 12px; }
    .review select { margin: 0; }
    textarea { box-sizing: border-box; width: 100%; padding: 8px 10px; font: inherit; font-size: 13px; color: var(--ink); background: var(--panel); border: 1px solid var(--input-line); border-radius: 4px; resize: vertical; }
    textarea:focus-visible { outline: 2px solid var(--focus); }
    ol { list-style: none; margin: 0; padding: 0; }
    .transition, .reason { margin: 6px 0 0; font-size: 12px; overflow-wrap: anywhere; white-space: pre-wrap; }
    .transition { color: var(--muted); }
    .heading h4 { margin: 0; }
    .history .heading { margin-bottom: 8px; }
    .review .actions, .upload-form .actions { justify-content: flex-end; flex-wrap: wrap; }
    .upload-type { display: grid; gap: 6px; width: 240px; max-width: 100%; margin: 12px 0; }
    .upload-type select { box-sizing: border-box; width: 100%; margin: 0; }
    .upload-file { display: grid; gap: 6px; min-width: 0; margin-bottom: 14px; }
    .upload-file input { box-sizing: border-box; width: 100%; max-width: 100%; min-width: 0; margin: 0; }
    @media (max-width: 400px) { li { flex-wrap: wrap; } .file-info { flex-basis: calc(100% - 30px); } li .actions { margin-left: auto; } }
  `,
})
export class RecordDocuments implements OnChanges {
  readonly type = input.required<'members' | 'dependents' | 'claims' | 'guarantee-letters'>();
  readonly recordId = input<number>();
  readonly authorization = input.required<string>();
  readonly documents = signal<RecordDocument[]>([]);
  readonly opened = signal(false);
  readonly loaded = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly notice = signal('');
  readonly pendingDelete = signal<RecordDocument | null>(null);
  readonly uploadFormOpen = signal(false);
  readonly uploadType = signal<DocumentType | ''>('');
  readonly selectedFile = signal<File | null>(null);
  readonly documentTypes: { value: DocumentType; label: string }[] = [
    { value: 'ID_CARD', label: 'ID card' },
    { value: 'PASSPORT', label: 'Passport' },
    { value: 'MEDICAL_PRESCRIPTION', label: 'Medical prescription' },
    { value: 'INVOICE', label: 'Invoice' },
    { value: 'OTHER', label: 'Other document' },
  ];
  readonly reviewDocument = signal<RecordDocument | null>(null);
  readonly reviewStatus = signal<DocumentValidationStatus>('VALIDATED');
  readonly reviewReason = signal('');
  readonly validationStatuses: DocumentValidationStatus[] = ['PENDING', 'VALIDATED', 'REJECTED'];
  readonly historyOpen = signal(false);
  readonly historyDocument = signal<RecordDocument | null>(null);
  readonly historyEvents = signal<DocumentValidationEvent[]>([]);
  readonly i18n = inject(I18nService);
  private readonly http = inject(HttpClient);
  private readonly browserDocument = inject(DOCUMENT);
  private revision = 0;

  constructor() { inject(DestroyRef).onDestroy(() => this.revision++); }

  ngOnChanges(): void {
    this.revision++;
    this.opened.set(false);
    this.loaded.set(false);
    this.busy.set(false);
    this.documents.set([]);
    this.error.set('');
    this.notice.set('');
    this.pendingDelete.set(null);
    this.resetUpload();
    this.reviewDocument.set(null);
    this.reviewReason.set('');
    this.historyOpen.set(false);
    this.historyDocument.set(null);
    this.historyEvents.set([]);
  }

  private path(): string { return `/api/v1/documents/${this.type()}/${this.recordId()}`; }
  private headers(): HttpHeaders { return new HttpHeaders({ Authorization: this.authorization() }); }

  async toggle(): Promise<void> {
    this.opened.update(value => !value);
    if (this.opened() && !this.loaded()) await this.load();
  }

  async load(): Promise<void> {
    if (this.busy() || !this.recordId()) return;
    this.reviewDocument.set(null);
    this.historyOpen.set(false);
    const revision = this.revision;
    this.busy.set(true);
    this.error.set('');
    try {
      const documents = await firstValueFrom(this.http.get<RecordDocument[]>(this.path(), { headers: this.headers() }));
      if (revision !== this.revision) return;
      this.documents.set(documents);
      this.loaded.set(true);
    } catch (error) {
      if (revision === this.revision) this.error.set(this.errorMessage(error, 'Unable to load documents.'));
    } finally {
      if (revision === this.revision) this.busy.set(false);
    }
  }

  openUploadForm(): void {
    if (this.busy() || !this.loaded() || !this.recordId() || this.uploadFormOpen()) return;
    this.resetUpload();
    this.uploadFormOpen.set(true);
    this.reviewDocument.set(null);
    this.pendingDelete.set(null);
    this.historyOpen.set(false);
    this.error.set('');
    this.notice.set('');
  }

  cancelUpload(): void {
    if (this.busy()) return;
    this.resetUpload();
    this.error.set('');
  }

  private resetUpload(): void {
    this.uploadFormOpen.set(false);
    this.uploadType.set('');
    this.selectedFile.set(null);
  }

  selectFile(event: Event): void {
    if (!this.uploadFormOpen() || !this.uploadType() || this.busy()) return;
    const picker = event.target as HTMLInputElement;
    const file = picker.files?.[0];
    this.selectedFile.set(null);
    this.error.set('');
    this.notice.set('');
    if (!file) return;
    const error = this.fileError(file);
    if (error) {
      this.error.set(error);
      picker.value = '';
      return;
    }
    this.selectedFile.set(file);
  }

  private fileError(file: File): string {
    if (file.size === 0) return 'Select a non-empty document.';
    if (file.size > 10 * 1024 * 1024) return 'Document must not exceed 10 MB.';
    return ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type) ? '' : 'Only PDF, JPEG and PNG documents are supported.';
  }

  async upload(): Promise<void> {
    const file = this.selectedFile();
    if (!this.uploadFormOpen() || !this.uploadType() || !file || this.busy() || !this.loaded() || !this.recordId()) return;
    this.error.set('');
    this.notice.set('');
    const error = this.fileError(file);
    if (error) { this.error.set(error); return; }
    const revision = this.revision;
    const body = new FormData();
    body.append('file', file);
    body.append('documentType', this.uploadType());
    this.busy.set(true);
    try {
      const document = await firstValueFrom(this.http.post<RecordDocument>(this.path(), body, { headers: this.headers() }));
      if (revision !== this.revision) return;
      this.documents.update(documents => [document, ...documents]);
      this.resetUpload();
      this.notice.set('Document uploaded.');
    } catch (error) {
      if (revision === this.revision) this.error.set(this.errorMessage(error, 'Unable to upload document.'));
    } finally {
      if (revision === this.revision) this.busy.set(false);
    }
  }

  async download(document: RecordDocument): Promise<void> {
    if (this.busy()) return;
    const revision = this.revision;
    this.busy.set(true);
    this.error.set('');
    try {
      const blob = await firstValueFrom(this.http.get(this.path() + `/${document.id}/content`, { headers: this.headers(), responseType: 'blob' }));
      if (revision !== this.revision) return;
      const url = URL.createObjectURL(blob);
      const link = this.browserDocument.createElement('a');
      link.href = url;
      link.download = document.filename;
      this.browserDocument.body.appendChild(link);
      try { link.click(); } finally { link.remove(); URL.revokeObjectURL(url); }
    } catch (error) {
      if (revision === this.revision) this.error.set(this.errorMessage(error, 'Unable to download document.'));
    } finally {
      if (revision === this.revision) this.busy.set(false);
    }
  }

  async remove(): Promise<void> {
    const document = this.pendingDelete();
    if (!document?.deletable || this.busy()) return;
    const revision = this.revision;
    this.busy.set(true);
    this.error.set('');
    this.notice.set('');
    try {
      await firstValueFrom(this.http.delete(this.path() + `/${document.id}`, { headers: this.headers() }));
      if (revision !== this.revision) return;
      this.documents.update(documents => documents.filter(item => item.id !== document.id));
      this.pendingDelete.set(null);
      if (this.reviewDocument()?.id === document.id) this.reviewDocument.set(null);
      this.notice.set('Document deleted.');
    } catch (error) {
      if (revision === this.revision) this.error.set(this.errorMessage(error, 'Unable to delete document.'));
    } finally {
      if (revision === this.revision) this.busy.set(false);
    }
  }

  setUploadType(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as DocumentType;
    if (this.documentTypes.some(option => option.value === value)) this.uploadType.set(value);
  }

  documentTypeLabel(type: DocumentType): string {
    return this.documentTypes.find(option => option.value === type)?.label ?? 'Other document';
  }

  openReview(document: RecordDocument): void {
    if (this.busy() || !document.canValidate) return;
    this.resetUpload();
    this.reviewDocument.set(document);
    this.reviewStatus.set(document.validationStatus === 'PENDING' ? 'VALIDATED' : 'PENDING');
    this.reviewReason.set('');
    this.historyOpen.set(false);
    this.error.set('');
    this.notice.set('');
  }

  setReviewStatus(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as DocumentValidationStatus;
    if (this.validationStatuses.includes(value)) this.reviewStatus.set(value);
  }

  setReviewReason(event: Event): void { this.reviewReason.set((event.target as HTMLTextAreaElement).value); }

  async saveValidation(): Promise<void> {
    const document = this.reviewDocument();
    const reason = this.reviewReason().trim();
    if (!document?.canValidate || this.busy() || !reason || reason.length > 1000 || this.reviewStatus() === document.validationStatus) return;
    const revision = this.revision;
    this.busy.set(true);
    this.error.set('');
    this.notice.set('');
    try {
      const updated = await firstValueFrom(this.http.put<RecordDocument>(this.path() + `/${document.id}/validation`, {
        status: this.reviewStatus(), version: document.version, reason,
      }, { headers: this.headers() }));
      if (revision !== this.revision) return;
      this.documents.update(documents => documents.map(item => item.id === updated.id ? updated : item));
      this.reviewDocument.set(null);
      this.notice.set('Document validation updated.');
    } catch (error) {
      if (revision === this.revision) this.error.set(this.errorMessage(error, 'Unable to update document validation.'));
    } finally {
      if (revision === this.revision) this.busy.set(false);
    }
  }

  async showHistory(document: RecordDocument | null = null): Promise<void> {
    if (this.busy() || !this.recordId()) return;
    this.resetUpload();
    const revision = this.revision;
    this.reviewDocument.set(null);
    this.historyDocument.set(document);
    this.historyOpen.set(true);
    this.historyEvents.set([]);
    this.error.set('');
    this.busy.set(true);
    try {
      const events = await firstValueFrom(this.http.get<DocumentValidationEvent[]>(
        this.path() + (document ? `/${document.id}` : '') + '/history', { headers: this.headers() }));
      if (revision === this.revision) this.historyEvents.set(events);
    } catch (error) {
      if (revision === this.revision) { this.historyOpen.set(false); this.error.set(this.errorMessage(error, 'Unable to load validation history.')); }
    } finally {
      if (revision === this.revision) this.busy.set(false);
    }
  }

  statusLabel(status: DocumentValidationStatus): string {
    return ({ PENDING: 'Document pending', VALIDATED: 'Document validated', REJECTED: 'Document rejected' })[status];
  }

  actionLabel(action: DocumentValidationEvent['action']): string {
    return ({ UPLOADED: 'Document uploaded', STATUS_CHANGED: 'Validation changed', DELETED: 'Document deleted' })[action];
  }

  sizeLabel(size: number): string {
    return new Intl.NumberFormat(this.i18n.locale(), { maximumFractionDigits: 1 }).format(size / 1024) + ' KB';
  }

  dateLabel(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : new Intl.DateTimeFormat(this.i18n.locale(), { dateStyle: 'short', timeStyle: 'short' }).format(date);
  }

  private errorMessage(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 413) return 'Document must not exceed 10 MB.';
      if (error.status === 415) return 'Only PDF, JPEG and PNG documents are supported.';
      if ([401, 403].includes(error.status)) return 'Document access denied.';
      if (error.status === 404) return 'Document or record not found.';
      if (error.status === 409) return 'Document changed. Refresh before reviewing again.';
    }
    return fallback;
  }
}